import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

const CACHE_TIME = 1000 * 60 * 4; // Cache search results for 4 minutes

const useSearchUsers = () => {
  const queryClient = useQueryClient(); // Gives direct access to TanStack Query's cache and query management system.

  const {
    mutate: searchUsers, // Function to trigger the search
    data: searchedUsers = [], // Users found from search, default to empty array
    isPending: isLoadingSearchedUsers, // Search is in progress
    error, // Error from search
    reset, // Clear search state
  } = useMutation({
    mutationFn: async (query) => {
      try {
        // Trim the query to remove extra spaces
        const trimmedQuery = query?.trim();

        // Validate input
        if (!trimmedQuery) {
          throw new Error("Enter a name to search.");
        }

        // Prepare cache key
        const cacheKey = ["searched-users", trimmedQuery.toLowerCase()];

        // Check if we already have a recent result in cache
        const cachedUsers = queryClient.getQueryData(cacheKey);
        // Return cached results
        if (cachedUsers) {
          return cachedUsers;
        }

        // Search for users
        const { data, error: supabaseError } = await supabase
          .from("users")
          .select("*")
          .ilike("full_name", `%${trimmedQuery}%`) // Case-insensitive search for users whose full_name contains the query
          .order("joined_at", { ascending: false });

        if (supabaseError) {
          throw supabaseError;
        }

        // No matching users found
        if (!data || data.length === 0) {
          throw new Error(`No users found matching "${trimmedQuery}".`);
        }

        // Store successful results in cache
        queryClient.setQueryData(cacheKey, data);

        // Automatically remove cache after 4 minutes
        setTimeout(() => {
          queryClient.removeQueries({ queryKey: cacheKey });
        }, CACHE_TIME);

        return data;
      } catch (error) {
        // Preserve existing error messages
        if (error instanceof Error) {
          throw error;
        }

        // Fallback for unexpected errors
        throw new Error("Something went wrong while searching users.");
      }
    },
  });

  return {
    searchUsers,
    searchedUsers,
    isLoadingSearchedUsers,
    searchError: error?.message ?? null,
    reset,
  };
};

export default useSearchUsers;
